import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import theme from 'theme';

interface WidthResizeHandlerProps {
  columnId: string;
  initialWidth?: number;
  onWidthChange: (columnId: string, newWidth: number) => void;
}

const WidthResizeHandler = ({ columnId, initialWidth, onWidthChange }: WidthResizeHandlerProps) => {
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(initialWidth || 0);
  const cellRef = useRef<HTMLElement | null>(null);
  const hasMovedRef = useRef<boolean>(false);
  const currentWidthRef = useRef<number>(0);
  const [, setColumnWidths] = useLocalStorage<Record<string, number>>('columnWidths', {});
  
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsResizing(true);
    hasMovedRef.current = false;
    startXRef.current = event.clientX;
    
    // Find the parent table cell
    const cell = (event.currentTarget.closest('td') || event.currentTarget.closest('th')) as HTMLElement;
    if (cell) {
      cellRef.current = cell;
      // Use the actual current width, or fall back to initialWidth or offsetWidth
      const currentWidth = initialWidth || cell.offsetWidth;
      startWidthRef.current = currentWidth;
      currentWidthRef.current = currentWidth;
    }
  };

  const updateAllColumnCells = (newWidth: number) => {
    if (!cellRef.current) {
      return;
    }

    // Find the table
    const table = cellRef.current.closest('table');
    if (!table) {
      return;
    }

    // Find the header row to get column index
    const headerRow = table.querySelector('thead tr');
    if (!headerRow) {
      return;
    }

    // Find the column index by finding which header cell contains our resize handler
    const headerCells = Array.from(headerRow.children);
    let columnIndex = -1;
    
    for (let i = 0; i < headerCells.length; i++) {
      const headerCell = headerCells[i] as HTMLElement;
      if (headerCell.contains(cellRef.current) || headerCell === cellRef.current) {
        columnIndex = i;
        break;
      }
    }

    if (columnIndex === -1) {
      return;
    }

    // Update all body cells in this column
    const bodyRows = table.querySelectorAll('tbody tr');
    bodyRows.forEach(row => {
      const cells = Array.from(row.children);
      if (cells[columnIndex]) {
        const bodyCell = cells[columnIndex] as HTMLElement;
        bodyCell.style.width = `${newWidth}px`;
        bodyCell.style.minWidth = `${newWidth}px`;
      }
    });
  };

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!cellRef.current) {
        return;
      }

      hasMovedRef.current = true;
      const diff = event.clientX - startXRef.current;
      const newWidth = Math.max(50, startWidthRef.current + diff); // Minimum width of 50px
      currentWidthRef.current = newWidth;
      
      // Update header cell inline styles immediately for smooth visual feedback
      cellRef.current.style.width = `${newWidth}px`;
      cellRef.current.style.minWidth = `${newWidth}px`;
      
      // Update all body cells in this column for smooth resizing
      updateAllColumnCells(newWidth);
    };

    const handleMouseUp = () => {
      if (!cellRef.current) {
        setIsResizing(false);
        return;
      }

      // Update state only on mouse up to avoid re-renders during drag
      if (hasMovedRef.current && currentWidthRef.current !== startWidthRef.current) {
        onWidthChange(columnId, currentWidthRef.current);
        // Save to localStorage
        setColumnWidths((prev: Record<string, number>) => ({
          ...prev,
          [columnId]: currentWidthRef.current,
        }));
      }
      
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, columnId, onWidthChange, setColumnWidths]);

  return (
    <Box
      style={{
        width: '16px',
        height: '100%',
        cursor: 'col-resize',
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        zIndex: 1,
      }}
      onMouseDown={handleMouseDown}
    >
      <Box style={{ width: '2px', height: '80%', backgroundColor: theme.palette.primary.main, margin: '0 auto' }} />
    </Box>
  );
};

export default WidthResizeHandler;